import axios from 'axios';

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  timeout: 30000,
});

// ── Token Refresh Queue ──────────────────────────────────────────────
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  failedQueue = [];
};

// ── Request Interceptor ──────────────────────────────────────────────
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ── Response Interceptor: Auto-refresh on 401 ───────────────────────
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Don't retry refresh or login endpoints (prevent infinite loops)
    if (
      !error.response ||
      error.response.status !== 401 ||
      originalRequest._retry ||
      originalRequest.url?.includes('/auth/refresh') ||
      originalRequest.url?.includes('/auth/verify-otp') ||
      originalRequest.url?.includes('/auth/send-otp')
    ) {
      // Non-401 or already retried or auth endpoint — just reject
      if (error.response?.status === 401 && !originalRequest.url?.includes('/auth/')) {
        // Session truly expired — clean up
        if (originalRequest._retry || !localStorage.getItem('refresh_token')) {
          _handleSessionExpired();
        }
      }
      return Promise.reject(error);
    }

    // If already refreshing, queue this request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
      }).catch((err) => Promise.reject(err));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      isRefreshing = false;
      _handleSessionExpired();
      return Promise.reject(error);
    }

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/refresh`,
        {},
        { headers: { 'X-Refresh-Token': refreshToken } }
      );

      const newToken = res.data.token;
      localStorage.setItem('token', newToken);
      if (res.data.user) {
        localStorage.setItem('user', JSON.stringify(res.data.user));
      }

      api.defaults.headers.common.Authorization = `Bearer ${newToken}`;
      processQueue(null, newToken);

      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return api(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      _handleSessionExpired();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

function _handleSessionExpired() {
  localStorage.removeItem('token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
  if (window.location.pathname !== '/login') {
    // Preserve intended destination
    const returnTo = window.location.pathname + window.location.search;
    if (returnTo !== '/' && returnTo !== '/login') {
      sessionStorage.setItem('returnTo', returnTo);
    }
    window.location.href = '/login?session_expired=1';
  }
}

export default api;
